import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Auth, authState } from '@angular/fire/auth';
import { DatasetSchema, SchemasService } from '../../../services/schemas.service';

type SortColumn = 'name' | 'columns' | 'updated_at';

@Component({
  selector: 'app-list-schemas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule
  ],
  templateUrl: './list-schemas.component.html',
  styleUrl: './list-schemas.component.scss'
})
export class ListSchemasComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  schemas: DatasetSchema[] = [];
  isLoading = true;

  searchText = '';
  sortColumn: SortColumn = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private schemasService: SchemasService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private auth: Auth
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params.get('access_denied') === 'schemas_admin') {
        this.snackBar.open(
          'Necesitas permisos de administrador de schemas para acceder a esa sección.',
          'Cerrar',
          { duration: 4500 }
        );
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { access_denied: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });

    authState(this.auth).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      void this.loadSchemas(user?.uid ?? null);
    });
  }

  get filteredSchemas(): DatasetSchema[] {
    const term = this.searchText.trim().toLowerCase();
    const filtered = !term
      ? this.schemas
      : this.schemas.filter((item) =>
          (item.name || '').toLowerCase().includes(term) ||
          (item.description || '').toLowerCase().includes(term)
        );

    const dir = this.sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const valueA = this.sortValue(a);
      const valueB = this.sortValue(b);
      if (valueA < valueB) return -1 * dir;
      if (valueA > valueB) return 1 * dir;
      return 0;
    });
  }

  setSort(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = column === 'updated_at' ? 'desc' : 'asc';
    }
  }

  private sortValue(item: DatasetSchema): string | number {
    switch (this.sortColumn) {
      case 'columns': return this.getColumnCount(item);
      case 'updated_at': return this.dateValue(item.updated_at);
      default: return (item.name || '').toLowerCase();
    }
  }

  getColumnCount(item: DatasetSchema): number {
    return item.columns.length || item.schema?.length || 0;
  }

  getRequiredColumnCount(item: DatasetSchema): number {
    const columns = item.columns.length ? item.columns : (item.schema ?? []);
    return columns.filter((column) => !!column.required).length;
  }

  formatUpdatedAt(value: unknown): string {
    const timestamp = this.dateValue(value);
    if (!timestamp) return 'Sin información';
    return new Date(timestamp).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  private dateValue(value: unknown): number {
    if (!value) return 0;
    if (typeof (value as any)?.toMillis === 'function') return (value as any).toMillis();
    if (typeof (value as any)?.seconds === 'number') return (value as any).seconds * 1000;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'string' || typeof value === 'number') {
      const timestamp = new Date(value).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    }
    return 0;
  }

  openSchema(schema: DatasetSchema): void {
    this.router.navigate(['/collector-datasets/create'], {
      queryParams: { schema_id: schema.id, schema_name: schema.name }
    });
  }

  private async loadSchemas(uid: string | null): Promise<void> {
    if (!uid) {
      this.schemas = [];
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    try {
      const access = await this.schemasService.getSchemaAccessByUid(uid);
      this.schemas = access.map((item) => {
        const raw = item.schemaRaw;
        const destinations = Array.isArray(raw['destinations']) ? (raw['destinations'] as any[]) : [];
        const columns = Array.isArray(raw['columns']) ? (raw['columns'] as any[]) : [];
        return {
          id: item.schemaId,
          name: String(raw['name'] ?? ''),
          description: raw['description'] ? String(raw['description']) : undefined,
          uid: raw['uid'] ? String(raw['uid']) : undefined,
          replace_scope: (raw['replace_scope'] as Record<string, unknown> | null | undefined) ?? null,
          destinations: destinations.map((destination) => ({
            type: String(destination?.type ?? ''),
            target: {
              table: String(destination?.target?.table ?? ''),
              schema: String(destination?.target?.schema ?? '')
            }
          })),
          rules: Array.isArray(raw['rules']) ? (raw['rules'] as any[]) : [],
          updated_at: raw['updated_at'],
          columns,
          schema: Array.isArray(raw['schema']) ? (raw['schema'] as any[]) : []
        };
      });
    } catch {
      this.schemas = [];
      this.snackBar.open('No se pudieron cargar los schemas.', 'Cerrar', {
        duration: 4000
      });
    } finally {
      this.isLoading = false;
    }
  }
}
